import authService from './auth.service';
import { PawnTicketsListResponse, PawnTicketFiltersParams, SmartLombardInstance } from '../types/smartlombard.types';

const API_URL = '/api/v1';

/**
 * Конвертирует ISO дату в формат YYYY-MM-DD для API
 */
const formatDateToYYYYMMDD = (dateStr?: string): string | undefined => {
  if (!dateStr) return undefined;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

class SmartLombardService {
  private buildQuery(params: Record<string, string | number | boolean | undefined>) {
    const query = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    return query ? `?${query}` : '';
  }

  async getInstances(params: { is_active?: boolean } = {}): Promise<SmartLombardInstance[]> {
    const query = this.buildQuery({ is_active: params.is_active });
    const url = `${API_URL}/integrations/providers/smartlombard/instances${query}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить список инстансов SmartLombard');
    }
    return res.json();
  }

  async createInstance(params: {
    name: string;
    account_id: string;
    secret_key: string;
    base_url: string;
  }): Promise<SmartLombardInstance> {
    const url = `${API_URL}/integrations/providers/smartlombard/instances`;
    const res = await authService.fetchWithAuth(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать инстанс SmartLombard');
    }
    return res.json();
  }

  async setInstanceActive(instanceId: number, isActive: boolean): Promise<SmartLombardInstance> {
    const url = `${API_URL}/integrations/providers/smartlombard/instances/${instanceId}/active`;
    const res = await authService.fetchWithAuth(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: isActive }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось изменить статус инстанса');
    }
    return res.json();
  }

  async getPawnTickets(params: PawnTicketFiltersParams & { instanceId?: number; limit?: number; offset?: number; id?: number } = {}): Promise<PawnTicketsListResponse> {
    const {
      instanceId = 10,
      limit = 20,
      offset = 0,
      id,
      identification_no,
      document_number,
      status,
      workplace_id,
      loan_amount_min,
      loan_amount_max,
      open_date_from,
      open_date_to,
    } = params;

    const query = this.buildQuery({
      limit,
      offset,
      id,
      identification_no,
      document_number,
      status,
      workplace_id,
      loan_amount_min,
      loan_amount_max,
      open_date_from: formatDateToYYYYMMDD(open_date_from),
      open_date_to: formatDateToYYYYMMDD(open_date_to),
    });

    const url = `${API_URL}/integrations/providers/smartlombard/instances/${instanceId}/pawn-tickets${query}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить залоговые билеты');
    }
    const data = (await res.json()) as PawnTicketsListResponse;
    return {
      total: Number((data as any).total ?? 0),
      items: Array.isArray((data as any).items) ? data.items : [],
    };
  }

  async getPawnTicketDetails(params: { instanceId?: number; id: number }): Promise<any> {
    const { instanceId = 10, id } = params;
    const url = `${API_URL}/integrations/providers/smartlombard/instances/${instanceId}/pawn-tickets/${id}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить залоговый билет');
    }
    return await res.json();
  }

  async getPawnTicketGoods(params: { instanceId?: number; id: number; limit?: number; offset?: number }): Promise<{ items: any[]; total?: number }> {
    const { instanceId = 10, id, limit = 100, offset = 0 } = params;
    const query = this.buildQuery({ limit, offset });
    const url = `${API_URL}/integrations/providers/smartlombard/instances/${instanceId}/pawn-tickets/${id}/goods${query}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить имущество залога');
    }
    const data = await res.json();
    // Сервер может вернуть массив без total
    if (Array.isArray(data)) return { items: data };
    return { items: Array.isArray((data as any).items) ? (data as any).items : [], total: Number((data as any).total ?? 0) };
  }

  async getWorkplaces(params: { instanceId?: number; limit?: number; offset?: number } = {}): Promise<{ total: number; items: any[] }> {
    const { instanceId = 10, limit = 100, offset = 0 } = params;
    const query = this.buildQuery({ limit, offset });
    const url = `${API_URL}/integrations/providers/smartlombard/instances/${instanceId}/workplaces${query}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить филиалы');
    }
    const data = await res.json();
    return {
      total: Number((data as any).total ?? 0),
      items: Array.isArray((data as any).items) ? (data as any).items : [],
    };
  }
}

export default new SmartLombardService();
